/**
 * The IANA time zones a picker offers, each named with its UTC offset right now.
 *
 * The list is asked of the runtime (`Intl.supportedValuesOf`), which knows every zone, so a
 * zone never needs a release to become pickable. A short list covers a runtime without it.
 * `UTC` is added on top because the runtime's list leaves it out, and it is a real answer.
 *
 * `current` is always kept, so a zone stored by hand or by an older build cannot vanish from
 * the field that is supposed to be showing it.
 *
 * PURE apart from reading the runtime's zone table.
 */

export interface TimezoneChoice {
  /** The IANA name that is stored, e.g. `America/New_York`. */
  value: string;
  /** What the picker prints and searches, e.g. `America/New York (UTC-4)`. */
  label: string;
}

const FALLBACK = ["Europe/Tallinn", "Europe/Helsinki", "Europe/Riga", "Europe/Vilnius", "Europe/Stockholm", "Europe/Berlin", "Europe/London"];

/** Every zone name, `UTC` first, with `current` kept even when the runtime does not list it. */
export function timezoneNames(current = ""): string[] {
  const supported = (Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  let all: string[];
  try {
    all = supported ? supported("timeZone") : FALLBACK;
  } catch {
    all = FALLBACK;
  }
  const names = all.includes("UTC") ? [...all] : ["UTC", ...all];
  const name = current.trim();
  return name && !names.includes(name) ? [name, ...names] : names;
}

/** `UTC+3`, `UTC+5:30`, `UTC-2:30` or `UTC` at `at`; "" for a name the runtime does not know. */
export function utcOffset(zone: string, at: Date): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName")?.value;
    if (!part) return "";
    const offset = part.replace(/^GMT/, "");
    return offset === "" || offset === "+0" || offset === "-0" ? "UTC" : `UTC${offset}`;
  } catch {
    return "";
  }
}

/** The picker's options, in the runtime's order (alphabetical), `UTC` first. */
export function timezoneChoices(current = "", at: Date = new Date()): TimezoneChoice[] {
  return timezoneNames(current).map((value) => {
    const offset = utcOffset(value, at);
    const name = value.replace(/_/g, " ");
    return { value, label: offset && offset !== name ? `${name} (${offset})` : name };
  });
}
